import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Dictionary, DictionaryConcept, DictionaryVersion } from 'app/mdm/terminology-lib/services/dictionary';

@Injectable()
export class DictionaryService {
  private baseUrl = `${environment.mdmApi}/dictionary`;

  constructor(private http: HttpClient) {}

  public getDictionaries(): Observable<Array<Dictionary>> {
    return this.http.get(`${this.baseUrl}`).pipe(map((r) => r as Array<Dictionary>));
  }

  public getDictionary(id: string, flat: boolean = false): Observable<Dictionary> {
    return this.http.get(`${this.baseUrl}/${id}${flat ? '?flat' : ''}`).pipe(
      map((r) => {
        const d = r as Dictionary;
        d.attributes = d.attributes || [];
        d.attributes.forEach((a) => (a.parameters = a.parameters || {}));
        return d;
      })
    );
  }

  public saveDictionary(d: Dictionary): Observable<any> {
    delete d.version;
    return this.http.put(`${this.baseUrl}/${d.id}`, d);
  }

  public getVersions(dictionary: string): Observable<DictionaryVersion[]> {
    return this.http.get(`${this.baseUrl}/${dictionary}/versions`).pipe(map((r) => r as DictionaryVersion[]));
  }

  public getVersion(dictionary: string, versionNumber: number, flat: boolean = false): Observable<DictionaryVersion> {
    return this.http.get(`${this.baseUrl}/${dictionary}/versions/${versionNumber}${flat ? '?flat' : ''}`).pipe(
      map((r) => {
        const vers = r as DictionaryVersion;
        if (!vers.concepts) {
          vers.concepts = [];
        }
        vers.concepts.forEach((c) => this.decorate(c));
        return vers;
      })
    );
  }

  public createVersion(dictionary: string, dictionaryVersion: DictionaryVersion): Observable<DictionaryVersion> {
    return this.http
      .post(`${this.baseUrl}/${dictionary}/versions`, dictionaryVersion)
      .pipe(map((r) => r as DictionaryVersion));
  }

  public updateVersion(
    dictionaryVersion: DictionaryVersion,
    dictionary: string,
    versionNumber: number
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/${dictionary}/versions/${versionNumber}`, dictionaryVersion);
  }

  public copyVersion(dictionary: string, versionNumber: number): Observable<DictionaryVersion> {
    return this.http
      .post(`${this.baseUrl}/${dictionary}/versions/${versionNumber}/copy`, null)
      .pipe(map((r) => r as DictionaryVersion));
  }

  public publishVersion(dictionary: string, versionNumber: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/${dictionary}/versions/${versionNumber}/publish`, null);
  }

  public deleteVersion(dictionary: string, versionNumber: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${dictionary}/versions/${versionNumber}`);
  }

  private decorate(concept: DictionaryConcept) {
    concept.attributes = concept.attributes || {};
    if (concept.children) {
      concept.children.forEach((c) => this.decorate(c));
    }
  }

  public createConcept(dictionary: string, versionNumber: number, concept: DictionaryConcept): Observable<any> {
    return this.http.post(`${this.baseUrl}/${dictionary}/versions/${versionNumber}/concepts/`, concept);
  }

  public updateConcept(dictionary: string, versionNumber: number, concept: DictionaryConcept): Observable<any> {
    return this.http.put(`${this.baseUrl}/${dictionary}/versions/${versionNumber}/concepts/${concept.code}`, concept);
  }

  public removeConcept(dictionary: string, versionNumber: number, concept: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${dictionary}/versions/${versionNumber}/concepts/${concept}`);
  }
}
